import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import { formatDate } from "../../utils/formatDate"

const KEYWORDS = {
  mauritius: ["mauritius", "maurice", "ile maurice", "tamarin", "grand baie", "mont choisy"],
  dubai: ["dubai", "difc", "downtown", "marina", "palm"],
  marbella: ["marbella", "spain", "espagne", "costa del sol"],
  france: ["france", "cote d'azur", "paris"],
  greece: ["greece", "grece", "mykonos", "athens"],
  unitedStates: ["united states", "usa", "miami", "new york", "los angeles"],
  maldives: ["maldives", "atoll", "male"],
}

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

const detectKey = (country = "") => {
  const v = normalize(country)
  if (v.includes("maurice") || v.includes("mauritius")) return "mauritius"
  if (v.includes("dubai") || v.includes("emirats")) return "dubai"
  if (v.includes("marbella") || v.includes("spain") || v.includes("espagne")) return "marbella"
  if (v.includes("france")) return "france"
  if (v.includes("greece") || v.includes("grece")) return "greece"
  if (v.includes("united states") || v.includes("etats-unis") || v.includes("usa")) return "unitedStates"
  if (v.includes("maldives")) return "maldives"
  return ""
}

const scorePost = (post, keywords = []) => {
  const hay = normalize(`${post?.title || ""} ${post?.category?.name || ""}`)
  return keywords.reduce((s, k) => (hay.includes(normalize(k)) ? s + 1 : s), 0)
}

const addUtm = (path, params) => {
  const qs = new URLSearchParams(params).toString()
  return `${path}${path.includes("?") ? "&" : "?"}${qs}`
}

// Image plein cadre de la carte : même recadrage qu'avant (cover), srcset
// adapté à la largeur de carte (280-560 px), proportions d'origine recadrées par
// object-cover comme gatsby-image, apparition en fondu doux.
const GuideImage = ({ url, alt }) => {
  const [loaded, setLoaded] = useState(false)
  const ref = useRef(null)
  // Image déjà arrivée avant l'hydratation : onLoad ne repassera pas.
  useEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth > 0) setLoaded(true)
  }, [])
  const src = width =>
    `${url}?fm=jpg&auto=compress&q=55&fit=max&w=${width}`
  return (
    <img
      ref={ref}
      src={src(800)}
      srcSet={`${src(400)} 400w, ${src(800)} 800w, ${src(1200)} 1200w`}
      sizes="(min-width: 1024px) 560px, 80vw"
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      className={`h-full w-full object-cover transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-[1.035] ${
        loaded ? "opacity-100" : "opacity-0"
      }`}
    />
  )
}

export default function LatestGuidesBlock({ locale = "en", currentCountryName = "" }) {
  const lang = locale === "fr" ? "fr" : "en"
  const data = useStaticQuery(graphql`
    query LatestGuidesForPropertyQuery {
      enPosts: allDatoCmsPost(
        filter: { locale: { eq: "en" }, slug: { ne: null } }
        sort: { fields: date, order: DESC }
        limit: 60
      ) {
        nodes {
          slug
          title
          date
          category {
            name
          }
          # URL seule (performance, 21/10/2026) : 120 articles chargés sur
          # chaque fiche pour en afficher 2 — l'image complète en était le poids.
          image {
            url
          }
        }
      }
      frPosts: allDatoCmsPost(
        filter: { locale: { eq: "fr" }, slug: { ne: null } }
        sort: { fields: date, order: DESC }
        limit: 60
      ) {
        nodes {
          slug
          title
          date
          category {
            name
          }
          # URL seule (performance, 21/10/2026) : 120 articles chargés sur
          # chaque fiche pour en afficher 2 — l'image complète en était le poids.
          image {
            url
          }
        }
      }
    }
  `)

  const posts = lang === "fr" ? data.frPosts.nodes : data.enPosts.nodes
  const key = detectKey(currentCountryName)
  const keywords = KEYWORDS[key] || []

  const guides = useMemo(() => {
    const scored = posts
      .map(post => ({ post, score: scorePost(post, keywords) }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 2)
      .map(item => item.post)
    if (scored.length >= 2) return scored
    const fallback = posts.filter(post => !scored.some(x => x.slug === post.slug))
    return scored.concat(fallback).slice(0, 2)
  }, [keywords, posts])

  if (!guides.length) return null

  const eyebrowText = lang === "fr" ? "Guides utiles" : "Useful guides"
  const titleText =
    lang === "fr"
      ? "Approfondir le marche."
      : "Go deeper into the market."
  const descriptionText =
    lang === "fr"
      ? "Analyses, tendances et reperes pratiques pour prolonger la visite par une lecture plus strategique."
      : "Analysis, trends, and practical guidance to extend the visit into a more strategic read."

  return (
    <section className="bg-white pb-12 md:pb-16 lg:pb-20">
      <div className="container">
        <div className="mx-auto max-w-[1120px] border-t border-black/10 pt-10 md:pt-14">
        <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(300px,0.48fr)] md:items-end">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#86868b]">
              {eyebrowText}
            </p>
            <h2 className="mt-3 max-w-3xl text-[32px] font-semibold leading-[1.05] text-[#1d1d1f] md:text-[48px]">
              {titleText}
            </h2>
          </div>
          <div className="max-w-md lg:justify-self-end">
            <p className="text-[15px] leading-[1.65] text-[#6e6e73] md:text-[16px]">
              {descriptionText}
            </p>
          </div>
        </div>

        <div className="mt-7 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:mt-10 [&::-webkit-scrollbar]:hidden">
          <div className="flex snap-x snap-mandatory gap-4 pr-4 touch-pan-x lg:grid lg:grid-cols-2 lg:gap-5 lg:pr-0">
          {guides.map(post => (
            <Link
              key={post.slug}
              to={addUtm(`/${lang}/magazine/${post.slug}`, {
                utm_source: "internal_property",
                utm_medium: "guides",
                utm_campaign: "education_to_conversion",
                utm_content: key || "property",
              })}
              className="group relative min-h-[380px] min-w-[280px] snap-start overflow-hidden rounded-[8px] bg-[#111] transition duration-500 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20 md:min-h-[410px] lg:min-w-0"
            >
              {post?.image?.url ? (
                <div className="absolute inset-0">
                  <GuideImage url={post.image.url} alt={post.title} />
                </div>
              ) : null}
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0.18)_36%,rgba(0,0,0,0.78)_100%)] transition duration-500 group-hover:bg-[linear-gradient(180deg,rgba(0,0,0,0.02)_0%,rgba(0,0,0,0.2)_34%,rgba(0,0,0,0.86)_100%)]" />
              <div className="relative flex h-full min-h-[380px] flex-col justify-between p-5 text-white sm:p-6 md:min-h-[410px] md:p-7">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-white/72">
                    {formatDate(post.date, lang)}
                  </p>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/14 text-[18px] leading-none text-white ring-1 ring-white/70 backdrop-blur transition group-hover:bg-white group-hover:text-[#1d1d1f] group-hover:ring-white">
                    &rarr;
                  </span>
                </div>
                <div className="max-w-[34rem]">
                  <p className="text-[25px] font-semibold leading-[1.04] text-white md:text-[32px]">
                    {post.title}
                  </p>
                  <span className="mt-5 inline-flex text-[14px] font-medium text-white/84 transition group-hover:text-white">
                    {lang === "fr" ? "Lire le guide" : "Read the guide"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
          </div>
        </div>
        </div>
      </div>
    </section>
  )
}
